import { useState, useEffect, useRef } from "react";
import {
  createLocalVideoTrack,
  createLocalAudioTrack,
  LocalVideoTrack,
  LocalAudioTrack,
} from "livekit-client";
import { useTranslation } from "react-i18next";
import { LeaveIcon } from "@livekit/components-react";

interface CustomPreJoinProps {
  onSubmit: (values: {
    username?: string;
    audioEnabled?: boolean;
    videoEnabled?: boolean;
    audioDeviceId?: string | null;
    videoDeviceId?: string | null;
  }) => void;
  onLeave?: () => void;
  defaults?: {
    username?: string;
    audioEnabled?: boolean;
    videoEnabled?: boolean;
  };
  videoResolution?: {
    width: number;
    height: number;
    frameRate?: number;
  };
  joinLabel?: string;
  micLabel?: string;
  camLabel?: string;
}

export function CustomPreJoin({
  onSubmit,
  onLeave,
  defaults = {},
  videoResolution,
  joinLabel = "Join Session",
}: CustomPreJoinProps) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  const videoWidth = videoResolution ? videoResolution.width / 2 : 540;
  const videoHeight = videoResolution ? videoResolution.height / 2 : 704;

  const [username] = useState(defaults.username || "donor");
  const [videoEnabled, setVideoEnabled] = useState(
    defaults.videoEnabled ?? true,
  );
  const [audioEnabled, setAudioEnabled] = useState(
    defaults.audioEnabled ?? true,
  );

  const [videoTrack, setVideoTrack] = useState<LocalVideoTrack | null>(null);
  const [audioTrack, setAudioTrack] = useState<LocalAudioTrack | null>(null);

  const [selectedVideoDevice, setSelectedVideoDevice] = useState<string>("");
  const [selectedAudioDevice, setSelectedAudioDevice] = useState<string>("");

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [audioLevel, setAudioLevel] = useState<number>(0);
  const [permissionsGranted, setPermissionsGranted] = useState(false);
  const [permissionError, setPermissionError] = useState<string>("");

  // Request permissions and get devices
  useEffect(() => {
    const requestPermissionsAndGetDevices = async () => {
      try {
        setIsLoading(true);
        setPermissionError("");

        console.log("🔐 Requesting camera and microphone permissions...");

        // Request permissions by accessing media
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });

        console.log("✅ Permissions granted!");

        // Stop the temporary stream immediately
        stream.getTracks().forEach((track) => track.stop());

        // Now enumerate devices (labels will be available after permission)
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoInputs = devices.filter((d) => d.kind === "videoinput");
        // filter out the default microphone because then we always have 2
        const audioInputs = devices.filter(
          (d) =>
            d?.kind === "audioinput" && d?.deviceId && d.deviceId !== "default",
        );

        console.log("📱 Available devices:", {
          video: videoInputs.length,
          audio: audioInputs.length,
        });

        if (videoInputs.length > 0) {
          setSelectedVideoDevice(videoInputs[0].deviceId);
        }
        if (audioInputs.length > 0) {
          setSelectedAudioDevice(audioInputs[0].deviceId);
        }

        setPermissionsGranted(true);
        setIsLoading(false);
      } catch (err: any) {
        console.error("❌ Failed to get permissions:", err);
        setPermissionError(
          `Permission denied: ${
            err.message || "Please allow camera and microphone access"
          }`,
        );
        setPermissionsGranted(false);
        setIsLoading(false);
      }
    };

    requestPermissionsAndGetDevices();
  }, []);

  // Create video track
  useEffect(() => {
    if (!permissionsGranted || !videoEnabled || !selectedVideoDevice) {
      if (videoTrack) {
        videoTrack.stop();
        setVideoTrack(null);
      }
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const createVideo = async () => {
      try {
        setIsLoading(true);
        setError("");

        if (videoTrack) {
          videoTrack.stop();
        }

        const options: any = {
          deviceId: selectedVideoDevice,
        };

        if (videoResolution) {
          options.resolution = videoResolution;
        }

        const track = await createLocalVideoTrack(options);

        // Debug: Check actual resolution
        const settings = track.mediaStreamTrack.getSettings();
        console.log("Actual video resolution:", {
          width: settings.width,
          height: settings.height,
        });

        if (cancelled) {
          track.stop();
          return;
        }

        setVideoTrack(track);

        if (videoRef.current) {
          track.attach(videoRef.current);
        }

        setIsLoading(false);
        setVideoEnabled(true);
      } catch (err: any) {
        console.error("Failed to create video track:", err);
        if (!cancelled) {
          setError(`Camera error: ${err.message}`);
          setIsLoading(false);
        }
      }
    };

    createVideo();

    return () => {
      cancelled = true;
    };
  }, [permissionsGranted, videoEnabled, selectedVideoDevice, videoResolution]);

  // Create audio track
  useEffect(() => {
    if (!permissionsGranted || !audioEnabled || !selectedAudioDevice) {
      if (audioTrack) {
        audioTrack.stop();
        setAudioTrack(null);
      }
      setAudioLevel(0);
      return;
    }

    let cancelled = false;

    const createAudio = async () => {
      try {
        if (audioTrack) {
          audioTrack.stop();
        }

        const track = await createLocalAudioTrack({
          deviceId: selectedAudioDevice,
        });

        if (cancelled) {
          track.stop();
          return;
        }

        setAudioTrack(track);
        setAudioEnabled(true);
      } catch (err: any) {
        console.error("Failed to create audio track:", err);
      }
    };

    createAudio();

    return () => {
      cancelled = true;
    };
  }, [permissionsGranted, audioEnabled, selectedAudioDevice]);

  // Audio level monitoring
  useEffect(() => {
    if (!audioTrack || !audioEnabled) {
      setAudioLevel(0);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      return;
    }

    console.log(audioEnabled);
    const setupAudioMonitoring = async () => {
      try {
        const mediaStream = new MediaStream([audioTrack.mediaStreamTrack]);
        const audioContext = new AudioContext();
        const source = audioContext.createMediaStreamSource(mediaStream);
        const analyser = audioContext.createAnalyser();

        analyser.fftSize = 512;
        analyser.smoothingTimeConstant = 0.3;
        source.connect(analyser);

        audioContextRef.current = audioContext;
        analyserRef.current = analyser;

        const dataArray = new Uint8Array(analyser.fftSize);

        const updateLevel = () => {
          if (!analyserRef.current) return;

          analyserRef.current.getByteTimeDomainData(dataArray);

          let sum = 0;
          for (let i = 0; i < dataArray.length; i++) {
            const normalized = (dataArray[i] - 128) / 128;
            sum += normalized * normalized;
          }
          const rms = Math.sqrt(sum / dataArray.length);
          const normalized = Math.min(rms * 5, 1);

          setAudioLevel(normalized);

          animationFrameRef.current = requestAnimationFrame(updateLevel);
        };

        updateLevel();
      } catch (err) {
        console.error("Failed to setup audio monitoring:", err);
      }
    };

    setupAudioMonitoring();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, [audioTrack, audioEnabled]);

  // Cleanup on unmount only
  useEffect(() => {
    return () => {
      videoTrack?.stop();
      audioTrack?.stop();
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  const handleSubmit = () => {
    onSubmit({
      username,
      audioEnabled,
      videoEnabled,
      audioDeviceId: selectedAudioDevice || null,
      videoDeviceId: selectedVideoDevice || null,
    });

    videoTrack?.stop();
    audioTrack?.stop();
  };

  // Show permission error state
  if (permissionError) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "1.5rem",
          padding: "2rem",
          maxWidth: "600px",
          margin: "0 auto",
          textAlign: "center",
          minHeight: "400px",
        }}
      >
        <div
          style={{
            fontSize: "4rem",
          }}
        >
          🔒
        </div>
        <h2 style={{ color: "white", margin: 0 }}>
          Camera and Microphone Access Required
        </h2>
        <p style={{ color: "rgba(255, 255, 255, 0.7)", margin: 0 }}>
          {permissionError}
        </p>
        <p style={{ color: "rgba(255, 255, 255, 0.6)", fontSize: "0.9rem" }}>
          Please allow access to your camera and microphone in your browser
          settings and refresh the page.
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: "1rem 2rem",
            borderRadius: "8px",
            border: "none",
            background: "rgba(147, 51, 234, 0.8)",
            color: "white",
            fontSize: "1rem",
            fontWeight: "600",
            cursor: "pointer",
            boxShadow: "0 4px 12px rgba(147, 51, 234, 0.3)",
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  // Show loading state while requesting permissions
  if (!permissionsGranted) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "1.5rem",
          padding: "2rem",
          minHeight: "400px",
        }}
      >
        <div
          style={{
            fontSize: "4rem",
          }}
        >
          <img src="/camera-on.svg" alt="" style={{ filter: "invert(100%)" }} />
        </div>
        <p style={{ color: "white", fontSize: "1.2rem" }}>
          Requesting camera and microphone access...
        </p>
        <p style={{ color: "rgba(255, 255, 255, 0.6)", fontSize: "0.9rem" }}>
          Please allow access in your browser
        </p>
      </div>
    );
  }

  const handleLeave = () => {
    onLeave?.();
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "1.5rem",
        padding: "2rem",
        maxWidth: "80vw",
        margin: "0 auto",
      }}
    >
      {/* Video */}
      <div
        style={{
          position: "relative",
          width: "100%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        {videoEnabled ? (
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            style={{
              aspectRatio: "16/9",
              width: `${videoWidth}px`,
              height: `${videoHeight}px`,
              objectFit: "contain",
              background: "#000",
              borderRadius: "12px",
              boxShadow: "0 10px 30px rgba(0, 0, 0, 0.5)",
              transform: "scaleX(-1)",
            }}
          />
        ) : (
          <div
            style={{
              width: `${videoWidth}px`,
              height: `${videoHeight}px`,
              background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              fontSize: "4rem",
            }}
          >
            <img
              src="/camera-off.svg"
              alt=""
              style={{ filter: "invert(100%)", width: "42px", height: "42px" }}
            />
          </div>
        )}

        {isLoading && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              color: "white",
            }}
          >
            Loading...
          </div>
        )}

        {error && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              color: "#ff6b6b",
              background: "rgba(0,0,0,0.8)",
              padding: "1rem",
              borderRadius: "8px",
              textAlign: "center",
            }}
          >
            ⚠️ {error}
          </div>
        )}
      </div>

      {/* Controls */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          width: "100%",
          maxWidth: "800px",
        }}
        className="lk-prejoin"
      >
        <div
          data-lk-theme="default"
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "1rem",
            width: "100%",
          }}
        >
          {/* Leave/Hang up Button */}
          <button
            className="lk-disconnect-button"
            onClick={handleLeave}
            style={{
              display: "flex",
              alignItems: "center",
              whiteSpace: "nowrap",
              height: "50px",
              padding: "10px 1.5rem",
            }}
          >
            <LeaveIcon className="mr-1" />
            {t("controls.leave")}
          </button>

          {/* Join Button */}
          <button
            onClick={handleSubmit}
            disabled={isLoading || !!error}
            className="lk-button lk-join-button"
            style={{
              flex: 1, // Take remaining space
              whiteSpace: "nowrap",
              height: "50px",
              padding: "10px 1.5rem",
            }}
          >
            {joinLabel}
          </button>

          {/* Audio Level Indicator */}
          {audioEnabled && (
            <div
              style={{
                display: "flex",
                alignItems: "flex-end",
                gap: "3px",
                background: "rgba(0, 0, 0, 0.3)",
                borderRadius: "6px",
                height: "50px",
                padding: "10px 0.7rem",
              }}
            >
              {/* Lower thresholds for more sensitive detection */}
              {[0.05, 0.1, 0.15, 0.2, 0.3].map((threshold, i) => (
                <div
                  key={i}
                  style={{
                    width: "4px",
                    height: `${8 + i * 6}px`,
                    background:
                      audioLevel > threshold
                        ? "white"
                        : "rgba(255, 255, 255, 0.2)",
                    borderRadius: "2px",
                    transition: "background 0.1s ease", // Faster transition
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
